import React, { useState } from 'react';
import { Inventory, War } from '../types';
import { SupabaseService } from '../services/supabaseService';
import { Crown, ChevronRight, Swords } from 'lucide-react';

interface RoyalConscriptionProps {
    war: War;
    isHost: boolean;
    onUpdateInventory: () => void;
    kingsLost?: number;
}

const PIECE_ICONS: Record<string, string> = {
    n: '♞', b: '♝', r: '♜', q: '♛', k: '♚'
};

const UNIT_LABELS: Record<string, string> = {
    n: 'KNIGHT', b: 'BISHOP', r: 'ROOK', q: 'QUEEN', k: 'KING'
};

// "Sacrifice the commons" - Costs in Pawns
const BASE_PAWN_COSTS: Record<string, number> = {
    n: 6,
    b: 6,
    r: 8,
    q: 12,
    k: 1 // Nominal cost to elect a new leader if fallen
};

const calculateDynamicCost = (type: string, currentCount: number): number => {
    if (type === 'k') return BASE_PAWN_COSTS.k;

    const base = BASE_PAWN_COSTS[type] || 6;
    let surcharge = 0;

    if (type === 'q') {
        // Queens: Up by 3 for EVERY one owned
        surcharge = currentCount * 3;
    } else {
        // Officers: Up by 3 for every TWO owned
        surcharge = Math.floor(currentCount / 2) * 3;
    }

    let total = base + surcharge;

    // Cap Officers at 20
    if (type !== 'q') {
        total = Math.min(20, total);
    }
    return total;
};

export const RoyalConscription: React.FC<RoyalConscriptionProps> = ({ war, isHost, onUpdateInventory, kingsLost = 0 }) => {
    const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
    const [isConfirming, setIsConfirming] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const currentInventory = isHost ? war.inventory : war.enemy_inventory;
    const availablePawns = currentInventory.p || 0;

    const handleSelect = (unitType: string) => {
        if (selectedUnit === unitType) {
            setSelectedUnit(null);
            setError(null);
        } else {
            setSelectedUnit(unitType);
            setError(null);
        }
    };

    const handleConscription = async () => {
        if (!selectedUnit) return;

        const currentCount = currentInventory[selectedUnit as keyof Inventory] || 0;
        const pawnCost = calculateDynamicCost(selectedUnit, currentCount);

        // Deserter Logic for King Election
        let deserterCount = 0;
        if (selectedUnit === 'k') {
            const rate = kingsLost * 0.15; // 15% per previous king lost
            deserterCount = Math.floor(availablePawns * rate);
        }

        const totalCost = pawnCost + deserterCount;

        // Validate
        if (availablePawns < totalCost) {
            setError(selectedUnit === 'k'
                ? `Insufficient Loyalty. Need ${totalCost} Pawns (Tribute: ${pawnCost} + Deserters: ${deserterCount})`
                : "Insufficient Conscripts (Pawns) to sacrifice.");
            return;
        }

        // Execute Transaction
        const newInventory = { ...currentInventory };

        // Deduct Pawns
        newInventory.p = (newInventory.p || 0) - totalCost;

        // Add Unit
        newInventory[selectedUnit as keyof Inventory] = (newInventory[selectedUnit as keyof Inventory] || 0) + 1;

        try {
            if (isHost) {
                await SupabaseService.updateInventory(war.id, newInventory);
            } else {
                await SupabaseService.updateEnemyInventory(war.id, newInventory);
            }

            // Reset
            setIsConfirming(false);
            setSelectedUnit(null);
            onUpdateInventory();
        } catch (err: any) {
            console.error("Conscription Failed", err);
            setError("Commission Rejected: " + (err.message || "Server Error"));
        }
    };

    return (
        <div className="w-full max-w-4xl mx-auto p-6 bg-tudor-dark/50 border border-tudor-gray rounded-lg">
            <div className="flex items-center justify-between mb-8 border-b border-tudor-gray pb-4">
                <div>
                    <h2 className="text-3xl font-serif text-tudor-gold tracking-widest uppercase">Royal Conscription</h2>
                    <p className="text-sm text-gray-400">Sacrifice the Commons to Knight the Elite.</p>
                </div>
                <div className="text-right">
                    <div className="text-xs text-tudor-gold/70 uppercase tracking-wider">Available Conscripts</div>
                    <div className="text-4xl font-mono text-tudor-text flex items-center justify-end gap-2">
                        <span>{availablePawns}</span>
                        <span className="text-2xl">♟</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* ELECT NEW MONARCH (If King is dead) */}
                {currentInventory.k === 0 && (
                    <div className="col-span-2 mb-4 bg-red-900/20 border-2 border-red-500/50 p-6 flex flex-col items-center justify-center animate-pulse">
                        <div className="flex items-center gap-4 mb-4">
                            <Crown className="text-red-500 w-12 h-12" />
                            <div className="text-center">
                                <h3 className="text-xl font-bold text-red-500 uppercase tracking-widest">The Throne is Empty</h3>
                                <p className="text-sm text-red-300">A new lineage must be established.</p>
                            </div>
                        </div>
                        <button
                            onClick={() => handleSelect('k')}
                            disabled={availablePawns < BASE_PAWN_COSTS.k}
                            className={`
                                px-8 py-3 bg-red-800 text-white font-bold tracking-widest uppercase hover:bg-red-700 transition-colors border border-red-500
                                ${availablePawns < BASE_PAWN_COSTS.k ? 'opacity-50 cursor-not-allowed' : ''}
                                ${selectedUnit === 'k' ? 'ring-2 ring-red-400 scale-105' : ''}
                             `}
                        >
                            Elect New Monarch (-{BASE_PAWN_COSTS.k} ♟)
                        </button>
                    </div>
                )}

                {/* SELECTION GRID */}
                <div className="grid grid-cols-2 gap-4">
                    {['n', 'b', 'r', 'q'].map(unit => {
                        const count = currentInventory[unit as keyof Inventory] || 0;
                        const cost = calculateDynamicCost(unit, count);
                        const canAfford = availablePawns >= cost;
                        const isSelected = selectedUnit === unit;

                        // Limit Queens to 1
                        const isMaxed = unit === 'q' && count >= 1;
                        const isDisabled = (!canAfford && !isMaxed) || isMaxed;

                        return (
                            <button
                                key={unit}
                                onClick={() => !isDisabled && handleSelect(unit)}
                                disabled={isDisabled}
                                className={`
                   relative p-4 border-2 transition-all duration-300 group
                   ${isSelected
                                        ? 'border-tudor-gold bg-tudor-gold/10 scale-105 shadow-[0_0_15px_rgba(197,160,89,0.3)]'
                                        : 'border-tudor-gray bg-tudor-black hover:border-tudor-gray/80'}
                   ${isDisabled ? 'opacity-40 cursor-not-allowed grayscale' : 'cursor-pointer'}
                 `}
                            >
                                <div className="absolute top-2 right-2 text-xs font-mono text-gray-500 flex items-center gap-1">
                                    {isMaxed ? <span className="text-red-500 font-bold">LOCKED</span> : <>{cost} <span className="text-[10px]">♟</span></>}
                                </div>
                                <div className="text-3xl mb-2 text-center text-tudor-text group-hover:text-tudor-gold transition-colors">
                                    {PIECE_ICONS[unit]}
                                </div>
                                <div className="text-center font-bold tracking-wider text-xs text-tudor-gold uppercase">
                                    {UNIT_LABELS[unit]}
                                </div>
                                <div className="text-[10px] text-zinc-500 text-center mt-1">
                                    Owned: {count} {unit === 'q' && '/ 1'}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* PREVIEW & CONFIRM */}
                <div className="bg-black/40 p-6 border border-tudor-gray/50 flex flex-col items-center justify-center text-center relative overflow-hidden">
                    {!selectedUnit ? (
                        <div className="text-gray-500 italic">
                            <Swords className="w-12 h-12 mx-auto mb-4 opacity-20" />
                            Select an elite unit to conscript
                        </div>
                    ) : (
                        <div className="w-full animate-fade-in-up">
                            <h3 className="text-xl font-bold text-tudor-gold mb-6 border-b border-tudor-gray/30 pb-4">
                                {selectedUnit === 'k' ? 'ELECTING NEW MONARCH' : `CONSCRIPTING ${UNIT_LABELS[selectedUnit]}`}
                            </h3>

                            {selectedUnit === 'k' && (kingsLost >= 1) && (
                                <div className="mb-4 bg-red-900/10 border border-red-500/50 p-2 text-sm text-red-400">
                                    <p className="font-bold uppercase mb-1">High Desertion Rate</p>
                                    <p>{Math.round(kingsLost * 0.15 * 100)}% of Pawns will flee the war.</p>
                                </div>
                            )}

                            <div className="flex items-center justify-center gap-4 mb-8 text-2xl font-mono">
                                {(() => {
                                    const cCount = currentInventory[selectedUnit as keyof Inventory] || 0;
                                    const pawnCost = calculateDynamicCost(selectedUnit, cCount);
                                    let deserterCount = 0;
                                    if (selectedUnit === 'k') {
                                        const rate = kingsLost * 0.15;
                                        deserterCount = Math.floor(availablePawns * rate);
                                    }
                                    const totalCost = pawnCost + deserterCount;

                                    return (
                                        <div className="flex flex-col items-center text-red-500">
                                            <span>-{totalCost}</span>
                                            <span className="text-2xl">♟</span>
                                            {deserterCount > 0 && <span className="text-xs text-red-400">({pawnCost} + {deserterCount} fled)</span>}
                                        </div>
                                    );
                                })()}
                                <ChevronRight className="text-gray-600" />
                                <div className="flex flex-col items-center text-green-500">
                                    <span>+1</span>
                                    <span className="text-2xl">{PIECE_ICONS[selectedUnit]}</span>
                                </div>
                            </div>

                            <button
                                onClick={handleConscription}
                                className="w-full bg-tudor-gold text-tudor-black font-bold py-4 hover:bg-yellow-600 transition-colors uppercase tracking-widest shadow-lg flex items-center justify-center gap-2"
                            >
                                <Swords size={18} />
                                Sacrifice & Conscript
                            </button>

                            {error && (
                                <p className="mt-4 text-red-500 text-sm animate-pulse">{error}</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
